import { Toast } from '@components/Toast';
import { faAngleLeft, faCodeBranch, faCopy, faCubes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfigurationContext } from '@partful/features/configurations/lib/configurationProvider';
import { useInstructionForConfiguration } from '@partful/features/instructions/root/lib/useInstructionForConfiguration';
import { ErrorMessage } from '@partful/features/instructions/ui/ErrorMessage';
import { useDuplicateInstructionVersion } from '@partful/features/instructions/versions/lib/useDuplicateInstructionVersion';
import { useInstructionVersion } from '@partful/features/instructions/versions/lib/useInstructionVersion';
import { useUpdateInstructionVersion } from '@partful/features/instructions/versions/lib/useUpdateInstructionVersion';
import { Badge } from '@partful/shared/ui/Badge';
import { Button } from '@partful/shared/ui/Button';
import { WizardPageContent, WizardPageHeader, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { LoadingSpinner } from '@partful/shared/ui/LoadingSpinner/LoadingSpinner';
import { Tab, TabPanel, Tabs, TabsList } from '@partful/shared/ui/Tabs';
import { Typography } from '@partful/shared/ui/Typography';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { InstructionTab } from './ui/InstructionsTab/InstructionsTab';
import { OverviewTab } from './ui/OverviewTab/OverviewTab';
import styles from './VersionPage.module.scss';

function parseTab(tab: string) {
  const tabs = ['overview', 'instructions'];
  return tabs.includes(tab) ? tab : 'overview';
}

export function VersionPage() {
  const { t: i18n } = useTranslation();
  const { versionId } = useParams<{ versionId: string }>();
  const [selectedTab, setSelectedTab] = useState(() => parseTab(window.location.hash.substring(1)));

  const configuration = useConfigurationContext();

  const {
    data: instruction,
    isLoading: isInstructionLoading,
    isError: isInstructionError,
  } = useInstructionForConfiguration(configuration);

  const {
    data: version,
    isLoading: isVersionLoading,
    isError: isVersionError,
  } = useInstructionVersion(instruction?.id, versionId);

  const {
    mutate: publishInstructionVersion,
    isLoading: isPublishing,
    isError: isPublishingError,
    isSuccess: isPublishingSuccess,
  } = useUpdateInstructionVersion();

  const handleChangeTab = useCallback((tab: string) => {
    const safeTab = parseTab(tab);
    window.location.hash = safeTab;
    setSelectedTab(safeTab);
  }, []);

  const handlePublish = useCallback(async () => {
    if (version) {
      publishInstructionVersion({
        instructionId: version.instructionId,
        versionId: version.id,
        updateArgs: {
          __expectedVersion: version.__version,
          status: version.status === 'draft' ? 'published' : 'draft',
        },
      });
    }
  }, [publishInstructionVersion, version]);

  const {
    mutate: duplicateInstructionVersion,
    isLoading: isDuplicating,
    isError: isDuplicatingError,
    isSuccess: isDuplicateSuccess,
  } = useDuplicateInstructionVersion();

  const handleDuplicate = useCallback(() => {
    if (instruction && version) {
      duplicateInstructionVersion({
        instructionId: version.instructionId,
        versionId: version.id,
        duplicateArgs: {
          authorName: version.authorName,
          authorUserId: version.authorUserId,
        },
      });
    }
  }, [duplicateInstructionVersion, instruction, version]);

  /**
   * Simplified states for rendering
   */
  const isError = isInstructionError || isVersionError;
  const isLoading = !isError && (isInstructionLoading || isVersionLoading);
  const eventNames = {
    draft: 'instructions_version_overview_publish_click',
    published: 'instructions_version_overview_to_draft_click',
  };

  return (
    <WizardPageLayout>
      <WizardPageHeader>
        <div>
          <Button linkComponent={Link} to={`/instructions/manage/${configuration.PID}/versions`}>
            <FontAwesomeIcon icon={faAngleLeft} />
            {i18n('common:backButton')}
          </Button>
        </div>
        <Typography appearance="heading5" as="h1">
          <FontAwesomeIcon icon={faCubes} /> {configuration.name}
        </Typography>
      </WizardPageHeader>
      <WizardPageContent className={styles.version}>
        {isError && (
          <ErrorMessage
            icon={<FontAwesomeIcon icon={faCodeBranch} />}
            title={i18n('instructions:manage.version.loadFailed.title')}
            message={i18n('instructions:manage.version.loadFailed.message')}
          />
        )}
        {isLoading && <LoadingSpinner className={styles.loading} />}
        {instruction && version && (
          <>
            <div className={styles.header}>
              <div className={styles.name}>
                <Typography as="h2" appearance="heading4">
                  {i18n('instructions:version.data.versionNumber.label', { number: version?.versionNumber })}
                </Typography>
                <Badge variant={/* istanbul ignore next */ version?.status === 'published' ? 'success' : 'default'}>
                  {i18n(`instructions:version.status.${version?.status}`)}
                </Badge>
              </div>
              <div className={styles.actions}>
                <Button
                  onClick={handlePublish}
                  name={
                    /* istanbul ignore next */ version.status === 'draft' || version.status === 'published'
                      ? eventNames[version.status]
                      : undefined
                  }
                >
                  {version.status === 'draft' &&
                    i18n(
                      isPublishing ? 'instructions:version.actions.publishing' : 'instructions:version.actions.publish'
                    )}
                  {version.status === 'published' &&
                    i18n(
                      isPublishing
                        ? 'instructions:version.actions.unpublishing'
                        : 'instructions:version.actions.unpublish'
                    )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDuplicate}
                  name="instructions_version_overview_duplicate_click"
                  disabled={isDuplicating}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  {i18n(
                    isDuplicating
                      ? 'instructions:version.actions.duplicating'
                      : 'instructions:version.actions.duplicate'
                  )}
                </Button>
              </div>
            </div>
            <div>
              <Tabs value={selectedTab} onChange={handleChangeTab}>
                <TabsList>
                  <Tab value="overview">{i18n('instructions:manage.version.overview')}</Tab>
                  <Tab value="instructions">{i18n('instructions:manage.version.instructions.title')}</Tab>
                </TabsList>
                <TabPanel value="overview" data-testid="version-overview-panel">
                  <OverviewTab version={version} />
                </TabPanel>
                <TabPanel value="instructions" data-testid="version-instructions-panel">
                  <InstructionTab version={version} />
                </TabPanel>
              </Tabs>
            </div>
          </>
        )}
        <Toast
          dependency={isPublishingError}
          severity="error"
          title={i18n(
            version?.status === 'draft'
              ? 'instructions:version.actions.publishError.title'
              : 'instructions:version.actions.unpublishError.title'
          )}
          message={i18n(
            version?.status === 'draft'
              ? 'instructions:version.actions.publishError.message'
              : 'instructions:version.actions.unpublishError.message'
          )}
          testId="publish-error-message"
        />
        <Toast
          dependency={isPublishingSuccess}
          severity="success"
          title={i18n(
            version?.status === 'draft'
              ? 'instructions:version.actions.unpublishSuccess.title'
              : 'instructions:version.actions.publishSuccess.title'
          )}
          testId="publish-success-message"
        />
        <Toast
          dependency={isDuplicatingError}
          severity="error"
          title={i18n('instructions:version.actions.duplicateError.title')}
          message={i18n('instructions:version.actions.duplicateError.message')}
          testId="duplicate-error-message"
        />
        <Toast
          dependency={isDuplicateSuccess}
          severity="success"
          title={i18n('instructions:version.actions.duplicateSuccess.title')}
          testId="duplicate-success-message"
        />
      </WizardPageContent>
    </WizardPageLayout>
  );
}
